<template>
  <section id="locations" class="saved-locations">
    <div class="saved-locations__heading">
      <div class="saved-locations__title-row">
        <h2 class="header-secondary__title">Taggable Accounts</h2>
        <InfoIconWithTooltip :width="280">These accounts can be used in image Tags and caption Mentions for your social posts.</InfoIconWithTooltip>
        <div class="saved-locations__search" @mouseenter="onSearchMouseEnter" @mouseleave="onSearchMouseLeave">
          <button type="button" class="saved-locations__search-toggle" aria-label="Search accounts" @click="openSearch">
            <IconEmbedded name="magnifier_2-5" :size="16" color="#667085" />
          </button>
          <div class="saved-locations__search-input-wrap" :class="{ 'saved-locations__search-input-wrap--open': isSearchExpanded }">
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              class="saved-locations__search-input global-text-input"
              type="text"
              placeholder="Search by name or handle"
              :tabindex="isSearchExpanded ? 0 : -1"
              @focus="isSearchFocused = true"
              @blur="onSearchBlur"
              @keydown.escape="clearAndCollapseSearch"
            />
          </div>
        </div>
        <SrpButton class="saved-locations__add-button" fill="outlined" size="small" color="teal" @click="openCreate">
          <template #icon><IconEmbedded name="plus_3" :size="18" /></template>
          Add Account
        </SrpButton>
        <!--<p>Business and locations to highlight in your posts.</p>-->
      </div>
      <div class="saved-locations__sort">
        <button type="button" class="saved-locations__sort-reverse" aria-label="Reverse sort order" @click="reverseSort">
          <IconEmbedded :name="sortIconName" :size="15" color="#5b5b5b" />
        </button>
        <SrpDropdown placement="bottom-end" :offset="3">
          <template #clickableElement="{ isDropdownVisible }">
            <div class="saved-locations__sort-trigger">
              <LinkWithIcon isDottedUnderline color="gray">
                {{ sortLabel }}
              </LinkWithIcon>
              <IconEmbedded :name="isDropdownVisible ? 'caret-top_3' : 'caret-bottom_3'" :size="14" color="#5b5b5b" style="margin-left: 2px" />
            </div>
          </template>
          <template #dropdown="{ close }">
            <div class="saved-locations__sort-dropdown">
              <div
                v-for="option in SORT_OPTIONS"
                :key="option.id"
                class="saved-locations__sort-option"
                :class="{ 'saved-locations__sort-option--selected': sortOption === option.id }"
                @click="setSortOption(option.id, close)"
              >
                <span>{{ option.label }}</span>
                <IconEmbedded v-if="sortOption === option.id" name="check_3" :size="16" color="rgba(0, 128, 96, 1)" />
              </div>
            </div>
          </template>
        </SrpDropdown>
      </div>
    </div>
    <Loader v-if="isLoading" />
    <template v-else-if="locations.length">
      <p v-if="normalizedSearchQuery && !filteredLocations.length" class="saved-locations__no-results">No accounts match your search.</p>
      <div v-else-if="displayedLocations.length" class="saved-locations__list">
        <div v-for="location in displayedLocations" :key="location.id" class="saved-locations__row">
          <LocationAvatar :src="location.icon" :name="location.name" :size="46" />
          <div class="saved-locations__copy">
            <div class="saved-locations__name-row">
              <strong>{{ location.name }}</strong>
              <TooltipWrapper v-if="location.website?.trim()" text="Website">
                <template #hoverableElement>
                  <a class="saved-locations__website-link" :href="websiteUrl(location)" target="_blank" rel="noopener noreferrer" aria-label="Website" @click.stop>
                    <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
                  </a>
                </template>
              </TooltipWrapper>
            </div>
            <div class="saved-locations__meta">
              <LocationSocialHandles :location="location" />
              <template v-if="location.googlePlaceId">
                <span v-if="hasSocialHandles(location)" class="saved-locations__sep"> · </span>
                <TooltipWrapper text="Location set">
                  <template #hoverableElement>
                    <a class="saved-locations__place-link" :href="googlePlaceUrl(location)" target="_blank" rel="noopener noreferrer" @click.stop>
                      <IconEmbedded name="map-pin_2" :size="14" color="#667085" />
                    </a>
                  </template>
                </TooltipWrapper>
              </template>
            </div>
            <div v-if="isDebugMode" class="saved-locations__debug-row">
              <span v-if="location.tagKind && location.tagKind !== 'Location'" class="saved-locations__fail-mode">{{ location.tagKind }}</span>
              <span class="saved-locations__fail-mode">FailMode: {{ location.failMode ?? "null" }}</span>
              <span class="saved-locations__fail-mode">id: {{ location.id }}</span>
              <LinkWithIcon v-if="isFBPending(location)" color="gray" isDottedUnderline @click="openFacebookVerification(location)">
                Verify Facebook
              </LinkWithIcon>
            </div>
          </div>
          <TooltipWrapper v-if="isAgeGatedPending(location)" text="Pending Manual Verification">
            <template #hoverableElement>
              <span class="saved-locations__verification-pending" aria-label="Pending Manual Verification">
                <IconEmbedded name="warning_2-5" :size="18" color="#b54708" />
              </span>
            </template>
          </TooltipWrapper>
          <button type="button" @click="openEdit(location)">Edit</button>
          <button type="button" class="saved-locations__archive" @click="archive(location)">Archive</button>
        </div>
      </div>
      <LinkWithIcon v-if="canToggleShowMore" class="saved-locations__show-more" isDottedUnderline color="gray" @click="isShowingAll = !isShowingAll">
        {{ isShowingAll ? "Show less" : `Show ${hiddenLocationCount} more` }}
      </LinkWithIcon>
    </template>
    <!--<button v-else type="button" class="saved-locations__empty" @click="openCreate">Add your first saved Location</button>-->
    <LocationEditorModal v-model:isVisible="isEditorVisible" :customerId="props.customerId" :location="locationToEdit" @saved="handleSaved" />
    <SrpModal v-model:isVisible="isArchiveModalVisible" size="small">
      <template #header>
        <h2 class="global-h2">Archive Account</h2>
      </template>
      <template #content>
        <p class="global-p">Archive {{ locationToArchive?.name }}?<br />Existing posts will retain their saved reference.</p>
      </template>
      <template #footer="{ closeModal }">
        <SrpButton color="gray" fill="outlined" :isDisabled="isArchiving" @click="closeModal">Cancel</SrpButton>
        <SrpButton color="teal" :isDisabled="isArchiving" @click="confirmArchive">Archive</SrpButton>
      </template>
    </SrpModal>
    <SrpModal v-model:isVisible="isVerificationModalVisible" size="medium" :isClosable="!isVerifying" :isCloseButtonVisible="!isVerifying">
      <template #header>
        <div class="saved-locations__verification-header">
          <h2 class="global-h2">Verify Facebook Handle</h2>
          <p class="global-p">Facebook blocks verification, manual loading often succeeds.</p>
        </div>
      </template>
      <template #content>
        <div class="saved-locations__verification-form">
          <p class="global-p">
            <strong>{{ locationToVerify?.name }}</strong
            ><br />
            Handle:
            <a :href="facebookVerificationUrl" target="_blank" rel="noopener noreferrer">{{ facebookVerificationUrl }}</a>
          </p>
          <label>
            Facebook page HTML
            <textarea
              v-model="verificationHtml"
              class="global-textarea saved-locations__verification-html"
              placeholder="Paste the Facebook page HTML here"
              :disabled="isVerifying"
              spellcheck="false"
            ></textarea>
          </label>
          <p v-if="verificationErrorMessage" class="global-error-string">{{ verificationErrorMessage }}</p>
        </div>
      </template>
      <template #footer>
        <SrpButton color="gray" fill="outlined" :isDisabled="isVerifying" @click="closeFacebookVerification">Cancel</SrpButton>
        <SrpButton color="teal" :isDisabled="isVerifying || !verificationHtml.trim()" @click="confirmFacebookVerification">
          {{ isVerifying ? "Verifying..." : "Verify" }}
        </SrpButton>
      </template>
    </SrpModal>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import TooltipWrapper from "@components/ui/TooltipWrapper.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import Loader from "@components/Loader/Loader.vue";
import LocationAvatar from "./LocationAvatar.vue";
import LocationSocialHandles from "./LocationSocialHandles.vue";
import LocationEditorModal from "./LocationEditorModal.vue";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import type { LocationData } from "@contracts/socialLocations";

type LocationSortOption = "newest" | "oldest" | "az" | "za";

const SORT_OPTIONS: Array<{ id: LocationSortOption; label: string }> = [
  { id: "az", label: "A to Z" },
  { id: "za", label: "Z to A" },
  { id: "newest", label: "newest first" },
  { id: "oldest", label: "oldest first" },
];

const LOCATION_SORT_STORAGE_KEY = "savedLocationsSort";
const DEFAULT_LOCATION_SORT_OPTION: LocationSortOption = "az";
const DEFAULT_VISIBLE_LOCATION_COUNT = 20;
const SEARCH_HOVER_OPEN_DELAY_MS = 150;

function isLocationSortOption(value: string | null): value is LocationSortOption {
  return value === "newest" || value === "oldest" || value === "az" || value === "za";
}

function readStoredSortOption(): LocationSortOption {
  if (typeof window === "undefined") return DEFAULT_LOCATION_SORT_OPTION;
  const stored = localStorage.getItem(LOCATION_SORT_STORAGE_KEY);
  return isLocationSortOption(stored) ? stored : DEFAULT_LOCATION_SORT_OPTION;
}

const props = withDefaults(
  defineProps<{
    customerId: string;
    isDebugMode?: boolean;
  }>(),
  { isDebugMode: false }
);

const locations = ref<LocationData[]>([]);
const isLoading = ref(false);
const isEditorVisible = ref(false);
const locationToEdit = ref<LocationData | null>(null);
const isArchiveModalVisible = ref(false);
const locationToArchive = ref<LocationData | null>(null);
const isArchiving = ref(false);
const isVerificationModalVisible = ref(false);
const locationToVerify = ref<LocationData | null>(null);
const verificationHtml = ref("");
const verificationErrorMessage = ref("");
const isVerifying = ref(false);
const facebookVerificationUrl = computed(() => {
  const handle = locationToVerify.value?.unverifiedFBHandle?.trim();
  return handle ? `https://www.facebook.com/${encodeURIComponent(handle)}` : "";
});

const searchQuery = ref("");
const searchInputRef = ref<HTMLInputElement | null>(null);
const isSearchHovered = ref(false);
const isSearchFocused = ref(false);
const isShowingAll = ref(false);
const sortOption = ref<LocationSortOption>(readStoredSortOption());

const sortLabel = computed(() => {
  const selected = SORT_OPTIONS.find(option => option.id === sortOption.value);
  const fallback = SORT_OPTIONS.find(option => option.id === DEFAULT_LOCATION_SORT_OPTION);
  return selected?.label ?? fallback?.label ?? "";
});
const sortIconName = computed(() => (sortOption.value === "newest" || sortOption.value === "az" ? "descending_3" : "ascending_3"));

const normalizedSearchQuery = computed(() => {
  let query = searchQuery.value.trim().toLowerCase();
  if (query.startsWith("@")) query = query.slice(1);
  return query;
});

const isSearchExpanded = computed(() => isSearchHovered.value || isSearchFocused.value || Boolean(searchQuery.value.trim()));

const filteredLocations = computed(() => {
  const query = normalizedSearchQuery.value;
  const source = query ? locations.value.filter(location => locationMatchesSearch(location, query)) : locations.value;
  return [...source].sort(compareLocations);
});

const displayedLocations = computed(() => {
  if (isShowingAll.value) return filteredLocations.value;
  return filteredLocations.value.slice(0, DEFAULT_VISIBLE_LOCATION_COUNT);
});

const hiddenLocationCount = computed(() => Math.max(0, filteredLocations.value.length - DEFAULT_VISIBLE_LOCATION_COUNT));

const canToggleShowMore = computed(() => filteredLocations.value.length > DEFAULT_VISIBLE_LOCATION_COUNT);

watch(searchQuery, () => {
  isShowingAll.value = false;
});

watch(sortOption, value => {
  localStorage.setItem(LOCATION_SORT_STORAGE_KEY, value);
});

onMounted(load);
async function load() {
  isLoading.value = true;
  try {
    locations.value = await SocialLocationsRepo.list(props.customerId, false, false);
  } finally {
    isLoading.value = false;
  }
}

function openCreate() {
  locationToEdit.value = null;
  isEditorVisible.value = true;
}
function openEdit(location: LocationData) {
  locationToEdit.value = location;
  isEditorVisible.value = true;
}
function archive(location: LocationData) {
  locationToArchive.value = location;
  isArchiveModalVisible.value = true;
}
async function confirmArchive() {
  if (!locationToArchive.value || isArchiving.value) return;
  isArchiving.value = true;
  try {
    await SocialLocationsRepo.archive(props.customerId, locationToArchive.value.id);
    isArchiveModalVisible.value = false;
    locationToArchive.value = null;
    await load();
  } finally {
    isArchiving.value = false;
  }
}
async function handleSaved() {
  await load();
}

function openFacebookVerification(location: LocationData) {
  locationToVerify.value = location;
  verificationHtml.value = "";
  verificationErrorMessage.value = "";
  isVerificationModalVisible.value = true;
}

function closeFacebookVerification() {
  if (isVerifying.value) return;
  isVerificationModalVisible.value = false;
  resetFacebookVerification();
}

async function confirmFacebookVerification() {
  const handle = locationToVerify.value?.unverifiedFBHandle?.trim();
  if (!handle || !verificationHtml.value.trim() || isVerifying.value) return;
  isVerifying.value = true;
  verificationErrorMessage.value = "";
  let succeeded = false;
  try {
    await SocialLocationsRepo.verifyFacebookPage(props.customerId, handle, verificationHtml.value);
    succeeded = true;
    isVerificationModalVisible.value = false;
    await load();
  } catch (error: any) {
    const value = error?.response?.data;
    verificationErrorMessage.value = typeof value === "string" ? value : value?.detail ?? value?.title ?? error?.message ?? "Unable to verify this Facebook Page.";
  } finally {
    isVerifying.value = false;
    if (succeeded) resetFacebookVerification();
  }
}

function resetFacebookVerification() {
  locationToVerify.value = null;
  verificationHtml.value = "";
  verificationErrorMessage.value = "";
}

function locationCreatedTime(location: LocationData): number {
  if (!location.createdDate) return 0;
  const parsed = Date.parse(location.createdDate);
  return Number.isFinite(parsed) ? parsed : 0;
}

function compareLocations(a: LocationData, b: LocationData): number {
  if (sortOption.value === "az") return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  if (sortOption.value === "za") return b.name.localeCompare(a.name, undefined, { sensitivity: "base" });
  const newestFirst = locationCreatedTime(b) - locationCreatedTime(a);
  return sortOption.value === "oldest" ? -newestFirst : newestFirst;
}

function reverseSort() {
  if (sortOption.value === "newest") sortOption.value = "oldest";
  else if (sortOption.value === "oldest") sortOption.value = "newest";
  else if (sortOption.value === "az") sortOption.value = "za";
  else sortOption.value = "az";
}

function setSortOption(option: LocationSortOption, close: () => void) {
  sortOption.value = option;
  close();
}

function locationMatchesSearch(location: LocationData, query: string): boolean {
  const socialSet = location.socialSet;
  const haystack = [location.name, socialSet?.instagramHandle, socialSet?.facebookPageName, location.unverifiedFBHandle, socialSet?.tikTokHandle].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(query);
}

function isFBPending(location: LocationData): boolean {
  return location.fbVerifyStatus === "Pending" && Boolean(location.unverifiedFBHandle?.trim()) && !location.socialSet?.facebookPageId;
}

function isAgeGatedPending(location: LocationData): boolean {
  return isFBPending(location) && location.failMode === "AgeGated";
}

let searchHoverOpenTimer: ReturnType<typeof setTimeout> | null = null;

function clearSearchHoverOpenTimer() {
  if (searchHoverOpenTimer) {
    clearTimeout(searchHoverOpenTimer);
    searchHoverOpenTimer = null;
  }
}

function onSearchMouseEnter() {
  if (isSearchHovered.value) return;
  clearSearchHoverOpenTimer();
  searchHoverOpenTimer = setTimeout(() => {
    isSearchHovered.value = true;
    searchHoverOpenTimer = null;
  }, SEARCH_HOVER_OPEN_DELAY_MS);
}

function onSearchMouseLeave() {
  clearSearchHoverOpenTimer();
  isSearchHovered.value = false;
}

async function openSearch() {
  clearSearchHoverOpenTimer();
  isSearchHovered.value = true;
  await nextTick();
  searchInputRef.value?.focus();
}

function onSearchBlur() {
  isSearchFocused.value = false;
}

function clearAndCollapseSearch() {
  clearSearchHoverOpenTimer();
  searchQuery.value = "";
  isSearchFocused.value = false;
  isSearchHovered.value = false;
  searchInputRef.value?.blur();
}

onUnmounted(clearSearchHoverOpenTimer);

function websiteUrl(location: LocationData): string {
  const website = location.website?.trim() ?? "";
  if (/^https?:\/\//i.test(website)) return website;
  return `https://${website}`;
}

function googlePlaceUrl(location: LocationData): string {
  let uri = "https://www.google.com/maps/search/?api=1";
  if (location.latitude != null && location.longitude != null) {
    uri += `&query=${location.latitude},${location.longitude}`;
  } else {
    uri += `&query=${encodeURIComponent(location.name)}`;
  }
  if (location.googlePlaceId) {
    uri += `&query_place_id=${encodeURIComponent(location.googlePlaceId)}`;
  }
  return uri;
}

function hasSocialHandles(location: LocationData): boolean {
  const socialSet = location.socialSet;
  return Boolean(socialSet?.instagramHandle || socialSet?.facebookPageName || socialSet?.tikTokHandle);
}
</script>

<style scoped lang="scss">
.saved-locations {
  margin-bottom: 30px;
  scroll-margin-top: 80px;
}
.saved-locations__heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 18px;
  margin-bottom: 12px;
}
.saved-locations__title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.saved-locations__heading h2,
.saved-locations__heading p {
  margin: 0;
}
.saved-locations__heading p {
  margin-top: 4px;
  color: #667085;
}
.saved-locations__search {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 28px;
}
.saved-locations__search-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
}
.saved-locations__search-input-wrap {
  width: 0;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
  transition:
    width 0.2s ease,
    opacity 0.2s ease;

  &--open {
    width: 220px;
    opacity: 1;
    pointer-events: auto;
  }
}
.saved-locations__search-input {
  width: 220px;
  height: 32px;
  padding: 4px 10px;
  font-size: 14px;
}
.saved-locations__add-button {
  margin-left: 12px;
}
.saved-locations__sort {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.saved-locations__sort-reverse {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
  position: relative;
  z-index: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
}
.saved-locations__sort-trigger {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  cursor: pointer;
}
.saved-locations__sort-dropdown {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
  min-width: 160px;
}
.saved-locations__sort-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background-color 0.1s ease;

  &:hover {
    background-color: rgba(0, 128, 128, 0.06);
  }

  &--selected {
    background-color: rgba(0, 128, 128, 0.08);
  }
}
.saved-locations__list {
  border: 1px solid #d8dce2;
  border-radius: 9px;
  overflow: hidden;
}
.saved-locations__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #e7e9ed;
}
.saved-locations__row:last-child {
  border-bottom: 0;
}
.saved-locations__copy {
  flex: 1;
  display: grid;
  gap: 3px;
}
.saved-locations__name-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.saved-locations__name-row :deep(.tooltip-wrapper),
.saved-locations__name-row :deep(.tooltip-wrapper__hoverable-element) {
  display: inline-flex;
  align-items: center;
}
.saved-locations__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 0;
}
.saved-locations__meta :deep(.tooltip-wrapper) {
  display: inline-flex;
  align-items: center;
}
.saved-locations__meta :deep(.tooltip-wrapper__hoverable-element) {
  display: inline-flex;
  align-items: center;
}
.saved-locations__sep {
  margin: 0 4px;
  color: #667085;
  font-size: 90%;
}
.saved-locations__fail-mode {
  color: #667085;
  font-size: 90%;
}
.saved-locations__debug-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.saved-locations__verification-form {
  display: grid;
  gap: 16px;
}
.saved-locations__verification-header {
  display: grid;
  gap: 4px;
}
.saved-locations__verification-header h2,
.saved-locations__verification-header p {
  margin: 0;
}
.saved-locations__verification-form p {
  margin: 0;
}
.saved-locations__verification-form label {
  display: grid;
  gap: 6px;
  font-weight: 600;
}
.saved-locations__verification-html {
  width: 100%;
  min-height: 320px;
  resize: vertical;
  font-family: monospace;
  font-weight: 400;
}
.saved-locations__verification-pending {
  display: inline-flex;
  align-items: center;
}
.saved-locations__place-link,
.saved-locations__website-link {
  display: inline-flex;
  align-items: center;
  position: relative;
  color: inherit;
  text-decoration: none;
}
.saved-locations__place-link:hover,
.saved-locations__website-link:hover {
  opacity: 0.75;
}
.saved-locations__row button {
  border: 0;
  background: transparent;
  color: #087f82;
  cursor: pointer;
}
.saved-locations__row .saved-locations__archive {
  color: #806468;
}
.saved-locations__empty {
  width: 100%;
  padding: 18px;
  border: 1px dashed #9aa5b1;
  border-radius: 8px;
  background: #fbfdfd;
  color: #087f82;
  cursor: pointer;
}
.saved-locations__no-results {
  margin: 8px 0 0;
  color: #667085;
}
.saved-locations__show-more {
  margin-top: 10px;
  margin-left: 8px;
  display: inline-flex;
  cursor: pointer;
}
</style>
